import {
  BarChartOutlined,
  DashboardOutlined,
  DollarOutlined,
  EyeOutlined,
  ProductOutlined,
  ReloadOutlined,
  RiseOutlined,
  SearchOutlined,
  ShoppingCartOutlined,
  TeamOutlined,
  TrophyOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  Col,
  Input,
  Row,
  Select,
  Spin,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
} from 'chart.js';
import React, { useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';

// Custom styles for enhanced design
const customStyles = `  
  .custom-table .ant-table-thead > tr > th {
    background-color: #f8fafc !important;
    color: #1f2937 !important;
    font-weight: 600 !important;
    border-bottom: 2px solid #e2e8f0 !important;
    padding: 12px 16px !important;
  }
  
  .custom-table .ant-table-thead > tr > th:hover {
    background-color: #f1f5f9 !important;
    color: #1f2937 !important;
  }
  
  .custom-table .ant-table-tbody > tr > td {
    border-bottom: 1px solid #e2e8f0 !important;
    color: #374151 !important;
    padding: 12px 16px !important;
    background-color: #ffffff !important;
  }
  
  .custom-table .ant-table-tbody > tr > td,
  .custom-table .ant-table-tbody > tr > td span,
  .custom-table .ant-table-tbody > tr > td div {
    color: #374151 !important;
  }
  
  .custom-table .ant-table-tbody > tr:hover > td {
    background-color: #f8fafc !important;
  }
  
  .custom-table .ant-table-tbody > tr:hover > td,
  .custom-table .ant-table-tbody > tr:hover > td span,
  .custom-table .ant-table-tbody > tr:hover > td div {
    color: #374151 !important;
  }
  
  .custom-table .ant-table-tbody > tr:hover > td .ant-tag {
    color: inherit !important;
  }
  
  .custom-table .ant-tag {
    color: inherit !important;
  }
  
  .custom-table .ant-table-cell {
    color: #374151 !important;
  }
  
  .custom-table .ant-table-cell,
  .custom-table .ant-table-cell span,
  .custom-table .ant-table-cell div {
    color: #374151 !important;
  }
  
  .custom-table .ant-table-wrapper {
    color: #374151 !important;
  }
  
  .custom-table .ant-table {
    color: #374151 !important;
  }
  
  .custom-table .ant-table-tbody > tr > td[data-index] {
    color: #374151 !important;
  }
  
  .custom-table .ant-table-tbody > tr > td .ant-table-cell-inner {
    color: #374151 !important;
  }
  
  .custom-table .ant-table-tbody > tr > td:nth-child(2) {
    color: #374151 !important;
  }
  
  .custom-table .ant-table-tbody > tr > td:nth-child(2) * {
    color: #374151 !important;
  }
  
  .ant-card {
    border-radius: 16px;
  }
  
  .ant-card-head {
    border-bottom: 1px solid #e2e8f0;
  }
  
  .ant-statistic-title {
    color: #64748b;
    font-weight: 500;
  }
  
  .ant-tag {
    border-radius: 8px;
    font-weight: 500;
  }
  
  .ant-btn-primary {
    border-radius: 8px;
    font-weight: 500;
  }
  
  .ant-input-affix-wrapper {
    border-radius: 8px;
  }
  
  .ant-select {
    border-radius: 8px;
  }
`;

import {
  generateMonthlyDateRanges,
  getDashboardStats,
  getMonthlySales,
  getProductWiseMachineOrderCount,
  getTopSellingCompanies,
  getTopSellingProductsMonthly,
} from '../api/analyticsApi';
import CommonLayout from '../common/CommonLayout';

const { Option } = Select;
const { Search } = Input;

function Home() {
  // Register Chart.js components only once
  useEffect(() => {
    try {
      ChartJS.register(
        CategoryScale,
        LinearScale,
        PointElement,
        LineElement,
        BarElement,
        Title,
        Tooltip,
        Legend,
      );
    } catch (error) {
      console.warn('Chart.js components already registered:', error);
    }
  }, []);
  const [productLoading, setProductLoading] = useState(true);
  const [monthlyLoading, setMonthlyLoading] = useState(true);
  const [companyLoading, setCompanyLoading] = useState(true);
  const [salesLoading, setSalesLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [productData, setProductData] = useState(null);
  const [monthlyData, setMonthlyData] = useState(null);
  const [companyData, setCompanyData] = useState(null);
  const [salesData, setSalesData] = useState(null);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(0); // Current month as default for products
  const [selectedCompanyMonth, setSelectedCompanyMonth] = useState(0); // Current month as default for companies
  const [productSearchText, setProductSearchText] = useState('');
  const [sparePartSearchText, setSparePartSearchText] = useState('');

  const monthlyRanges = generateMonthlyDateRanges();

  // Fetch dashboard statistics
  const fetchDashboardStats = async () => {
    try {
      setStatsLoading(true);
      const response = await getDashboardStats();
      if (response.code === 200) {
        setDashboardStats(response.data);
      } else {
        setError('Failed to fetch dashboard stats');
      }
    } catch (err) {
      setError('Error fetching dashboard stats: ' + err.message);
    } finally {
      setStatsLoading(false);
    }
  };

  // Fetch product-wise order data
  const fetchProductData = async () => {
    try {
      setProductLoading(true);
      const response = await getProductWiseMachineOrderCount();
      if (response.code === 200) {
        setProductData(response.data);
      } else {
        setError('Failed to fetch product data');
      }
    } catch (err) {
      setError('Error fetching product data: ' + err.message);
    } finally {
      setProductLoading(false);
    }
  };

  // Fetch monthly top selling products for selected month
  const fetchMonthlyData = async (monthIndex) => {
    try {
      setMonthlyLoading(true);
      const range = monthlyRanges[monthIndex];
      const response = await getTopSellingProductsMonthly(
        range.startDate,
        range.endDate,
      );
      if (response.code) {
        setMonthlyData({
          month: range.month,
          data: response.data || [],
          totalOrders:
            response.data?.reduce(
              (sum, item) => sum + item.totalOrderedQuantity,
              0,
            ) || 0,
        });
      } else {
        setError('Failed to fetch monthly data');
      }
    } catch (err) {
      setError('Error fetching monthly data: ' + err.message);
    } finally {
      setMonthlyLoading(false);
    }
  };

  // Fetch monthly top selling companies for selected month
  const fetchCompanyData = async (monthIndex) => {
    try {
      setCompanyLoading(true);
      const range = monthlyRanges[monthIndex];
      const response = await getTopSellingCompanies(
        range.startDate,
        range.endDate,
      );
      if (response.code === 200) {
        setCompanyData({
          month: range.month,
          data: response.data || [],
          totalAmount:
            response.data?.reduce(
              (sum, item) => sum + item.totalOrderAmount,
              0,
            ) || 0,
        });
      } else {
        setError('Failed to fetch company data');
      }
    } catch (err) {
      setError('Error fetching company data: ' + err.message);
    } finally {
      setCompanyLoading(false);
    }
  };

  // Fetch monthly sales data
  const fetchSalesData = async () => {
    try {
      setSalesLoading(true);
      const response = await getMonthlySales();
      if (response.code === 200) {
        setSalesData(response.data || []);
      } else {
        setError('Failed to fetch sales data');
      }
    } catch (err) {
      setError('Error fetching sales data: ' + err.message);
    } finally {
      setSalesLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardStats();
    fetchProductData();
    fetchMonthlyData(selectedMonth); // Fetch data for current month (default)
    fetchCompanyData(selectedCompanyMonth); // Fetch company data for current month (default)
    fetchSalesData(); // Fetch sales data
  }, []);

  // Fetch monthly data when month selection changes
  useEffect(() => {
    if (selectedMonth !== null) {
      fetchMonthlyData(selectedMonth);
    }
  }, [selectedMonth]);

  // Fetch company data when company month selection changes
  useEffect(() => {
    if (selectedCompanyMonth !== null) {
      fetchCompanyData(selectedCompanyMonth);
    }
  }, [selectedCompanyMonth]);

  // Filter and sort data based on search
  const getFilteredProducts = () => {
    if (!productData?.productList) return [];
    return productData.productList
      .filter((product) =>
        product.productName
          .toLowerCase()
          .includes(productSearchText.toLowerCase()),
      )
      .sort((a, b) => b.totalOrderedQuantity - a.totalOrderedQuantity);
  };

  const getFilteredSpareParts = () => {
    if (!productData?.sparePartList) return [];
    return productData.sparePartList
      .filter((part) =>
        part.sparePartName
          .toLowerCase()
          .includes(sparePartSearchText.toLowerCase()),
      )
      .sort((a, b) => b.totalOrderedQuantity - a.totalOrderedQuantity);
  };

  const getCurrentMonthProducts = () => {
    if (!monthlyData?.data) return [];
    return monthlyData.data.sort(
      (a, b) => b.totalOrderedQuantity - a.totalOrderedQuantity,
    );
  };

  const getFilteredCompanies = () => {
    if (!companyData?.data) return [];
    return companyData.data.sort(
      (a, b) => b.totalOrderAmount - a.totalOrderAmount,
    );
  };

  // Table columns for products
  const productColumns = [
    {
      title: 'S.No',
      key: 'serialNumber',
      width: 80,
      render: (text, record, index) => index + 1,
    },
    {
      title: 'Product Name',
      dataIndex: 'productName',
      key: 'productName',
      ellipsis: true,
    },
    {
      title: 'Total Orders',
      dataIndex: 'totalOrderedQuantity',
      key: 'totalOrderedQuantity',
      width: 120,
      sorter: (a, b) => a.totalOrderedQuantity - b.totalOrderedQuantity,
      defaultSortOrder: 'descend',
      render: (quantity) => (
        <Tag color={quantity > 10 ? 'green' : quantity > 5 ? 'blue' : 'orange'}>
          {quantity}
        </Tag>
      ),
    },
  ];

  // Table columns for spare parts
  const sparePartColumns = [
    {
      title: 'S.No',
      key: 'serialNumber',
      width: 80,
      render: (text, record, index) => index + 1,
    },
    {
      title: 'Part Name',
      dataIndex: 'sparePartName',
      key: 'sparePartName',
      ellipsis: true,
    },
    {
      title: 'Total Orders',
      dataIndex: 'totalOrderedQuantity',
      key: 'totalOrderedQuantity',
      width: 120,
      sorter: (a, b) => a.totalOrderedQuantity - b.totalOrderedQuantity,
      defaultSortOrder: 'descend',
      render: (quantity) => (
        <Tag
          color={quantity > 20 ? 'green' : quantity > 10 ? 'blue' : 'orange'}
        >
          {quantity}
        </Tag>
      ),
    },
  ];

  // Table columns for monthly products
  const monthlyProductColumns = [
    {
      title: 'S.No',
      key: 'serialNumber',
      width: 80,
      render: (text, record, index) => index + 1,
    },
    {
      title: 'Product Name',
      dataIndex: 'productName',
      key: 'productName',
      ellipsis: true,
    },
    {
      title: 'Orders This Month',
      dataIndex: 'totalOrderedQuantity',
      key: 'totalOrderedQuantity',
      width: 150,
      sorter: (a, b) => a.totalOrderedQuantity - b.totalOrderedQuantity,
      defaultSortOrder: 'descend',
      render: (quantity) => (
        <Tag color={quantity > 5 ? 'green' : quantity > 2 ? 'blue' : 'orange'}>
          {quantity}
        </Tag>
      ),
    },
  ];

  const handleRefresh = () => {
    fetchDashboardStats();
    fetchProductData();
    fetchMonthlyData(selectedMonth);
    fetchCompanyData(selectedCompanyMonth);
    fetchSalesData();
  };

  // Prepare chart data
  const getChartData = () => {
    if (!salesData || salesData.length === 0) return null;

    const monthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];

    const labels = salesData.map((item) => monthNames[item.month - 1]);
    const data = salesData.map((item) => item.totalSalesAmount);

    return {
      labels,
      datasets: [
        {
          label: 'Monthly Revenue (₹)',
          data: data,
          borderColor: 'rgb(99, 102, 241)',
          backgroundColor: 'rgba(99, 102, 241, 0.1)',
          borderWidth: 3,
          fill: true,
          tension: 0.4,
          pointBackgroundColor: 'rgb(99, 102, 241)',
          pointBorderColor: '#fff',
          pointBorderWidth: 3,
          pointRadius: 6,
          pointHoverRadius: 10,
          pointHoverBackgroundColor: 'rgb(99, 102, 241)',
          pointHoverBorderColor: '#fff',
          pointHoverBorderWidth: 3,
        },
      ],
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 14,
            weight: 'bold',
            family: 'Inter, sans-serif',
          },
          color: '#374151',
        },
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#1f2937',
        bodyColor: '#374151',
        borderColor: 'rgb(59, 130, 246)',
        borderWidth: 2,
        cornerRadius: 12,
        displayColors: false,
        titleFont: {
          size: 14,
          weight: 'bold',
        },
        bodyFont: {
          size: 13,
          weight: '500',
        },
        callbacks: {
          title: function (context) {
            return context[0].label;
          },
          label: function (context) {
            return `Revenue: ₹${context.parsed.y.toLocaleString()}`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
          drawBorder: false,
        },
        ticks: {
          callback: function (value) {
            return '₹' + (value / 100000).toFixed(1) + 'L';
          },
          font: {
            size: 12,
            family: 'Inter, sans-serif',
          },
          color: '#6b7280',
        },
      },
      x: {
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
          drawBorder: false,
        },
        ticks: {
          font: {
            size: 12,
            family: 'Inter, sans-serif',
          },
          color: '#6b7280',
        },
      },
    },
    interaction: {
      intersect: false,
      mode: 'index',
    },
    elements: {
      point: {
        hoverRadius: 8,
        radius: 4,
      },
    },
  };

  return (
    <CommonLayout>
      <style>{customStyles}</style>
      <div className='min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50'>
        <div className='space-y-8 p-6'>
          {/* Header */}
          <div className='bg-white rounded-2xl shadow-xl p-8 border border-gray-100'>
            <div className='flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6'>
              <div className='flex-1'>
                <div className='flex items-center gap-4 mb-4'>
                  <div className='p-3 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl shadow-lg'>
                    <DashboardOutlined className='text-white text-2xl' />
                  </div>
                  <div>
                    <h1 className='text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent'>
                      Analytics Dashboard
                    </h1>
                    <p className='text-gray-600 text-lg mt-2'>
                      Real-time insights into your business performance
                    </p>
                  </div>
                </div>
                {error && (
                  <Alert
                    message='Error'
                    description={error}
                    type='error'
                    showIcon
                    closable
                    onClose={() => setError(null)}
                    className='mb-4'
                  />
                )}
              </div>
              <div className='flex gap-3'>
                <Tooltip title='Refresh all data'>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={handleRefresh}
                    type='primary'
                    size='large'
                    className='bg-gradient-to-r from-blue-500 to-purple-600 border-none shadow-lg hover:shadow-xl transition-all duration-300'
                    loading={
                      statsLoading ||
                      productLoading ||
                      monthlyLoading ||
                      companyLoading ||
                      salesLoading
                    }
                  >
                    Refresh Data
                  </Button>
                </Tooltip>
              </div>
            </div>
          </div>

          {/* Dashboard Statistics Cards */}
          <Row gutter={[24, 24]}>
            <Col
              xs={24}
              sm={12}
              lg={6}
            >
              <Card
                className='h-full border-0 shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 bg-gradient-to-br from-blue-50 to-blue-100'
                styles={{ body: { padding: '24px' } }}
              >
                <div className='flex items-center justify-between'>
                  <div className='flex-1'>
                    <div className='flex items-center gap-3 mb-4'>
                      <div className='p-3 bg-blue-500 rounded-xl shadow-lg'>
                        <UserOutlined className='text-white text-xl' />
                      </div>
                      <div>
                        <p className='text-gray-600 text-sm font-medium'>
                          Total Users
                        </p>
                        <p className='text-3xl font-bold text-blue-600'>
                          {statsLoading ? (
                            <Spin size='small' />
                          ) : (
                            dashboardStats?.totalUsers || 0
                          )}
                        </p>
                      </div>
                    </div>
                    <div className='flex items-center gap-2'>
                      <RiseOutlined className='text-green-500' />
                      <span className='text-green-600 text-sm font-medium'>
                        Active users
                      </span>
                    </div>
                  </div>
                </div>
              </Card>
            </Col>
            <Col
              xs={24}
              sm={12}
              lg={6}
            >
              <Card
                className='h-full border-0 shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 bg-gradient-to-br from-green-50 to-green-100'
                styles={{ body: { padding: '24px' } }}
              >
                <div className='flex items-center justify-between'>
                  <div className='flex-1'>
                    <div className='flex items-center gap-3 mb-4'>
                      <div className='p-3 bg-green-500 rounded-xl shadow-lg'>
                        <ProductOutlined className='text-white text-xl' />
                      </div>
                      <div>
                        <p className='text-gray-600 text-sm font-medium'>
                          Total Products
                        </p>
                        <p className='text-3xl font-bold text-green-600'>
                          {statsLoading ? (
                            <Spin size='small' />
                          ) : (
                            dashboardStats?.totalProducts || 0
                          )}
                        </p>
                      </div>
                    </div>
                    <div className='flex items-center gap-2'>
                      <BarChartOutlined className='text-green-500' />
                      <span className='text-green-600 text-sm font-medium'>
                        In catalog
                      </span>
                    </div>
                  </div>
                </div>
              </Card>
            </Col>
            <Col
              xs={24}
              sm={12}
              lg={6}
            >
              <Card
                className='h-full border-0 shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 bg-gradient-to-br from-orange-50 to-orange-100'
                styles={{ body: { padding: '24px' } }}
              >
                <div className='flex items-center justify-between'>
                  <div className='flex-1'>
                    <div className='flex items-center gap-3 mb-4'>
                      <div className='p-3 bg-orange-500 rounded-xl shadow-lg'>
                        <ProductOutlined className='text-white text-xl' />
                      </div>
                      <div>
                        <p className='text-gray-600 text-sm font-medium'>
                          Spare Parts
                        </p>
                        <p className='text-3xl font-bold text-orange-600'>
                          {statsLoading ? (
                            <Spin size='small' />
                          ) : (
                            dashboardStats?.totalSpareParts || 0
                          )}
                        </p>
                      </div>
                    </div>
                    <div className='flex items-center gap-2'>
                      <EyeOutlined className='text-orange-500' />
                      <span className='text-orange-600 text-sm font-medium'>
                        Available parts
                      </span>
                    </div>
                  </div>
                </div>
              </Card>
            </Col>
            <Col
              xs={24}
              sm={12}
              lg={6}
            >
              <Card
                className='h-full border-0 shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 bg-gradient-to-br from-purple-50 to-purple-100'
                styles={{ body: { padding: '24px' } }}
              >
                <div className='flex items-center justify-between'>
                  <div className='flex-1'>
                    <div className='flex items-center gap-3 mb-4'>
                      <div className='p-3 bg-purple-500 rounded-xl shadow-lg'>
                        <TeamOutlined className='text-white text-xl' />
                      </div>
                      <div>
                        <p className='text-gray-600 text-sm font-medium'>
                          Admin Users
                        </p>
                        <p className='text-3xl font-bold text-purple-600'>
                          {statsLoading ? (
                            <Spin size='small' />
                          ) : (
                            dashboardStats?.adminUsers || 0
                          )}
                        </p>
                      </div>
                    </div>
                    <div className='flex items-center gap-2'>
                      <TrophyOutlined className='text-purple-500' />
                      <span className='text-purple-600 text-sm font-medium'>
                        Administrators
                      </span>
                    </div>
                  </div>
                </div>
              </Card>
            </Col>
          </Row>

          {/* Monthly Sales Chart */}
          <Row gutter={[24, 24]}>
            <Col xs={24}>
              <Card
                className='border-0 shadow-xl bg-white'
                title={
                  <div className='flex items-center gap-3'>
                    <div className='p-2 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg'>
                      <RiseOutlined className='text-white text-lg' />
                    </div>
                    <div>
                      <h3 className='text-xl font-bold text-gray-800'>
                        Monthly Sales Trend
                      </h3>
                      <p className='text-gray-500 text-sm'>
                        Revenue performance over time
                      </p>
                    </div>
                  </div>
                }
                extra={
                  <div className='flex items-center gap-2'>
                    <DollarOutlined className='text-green-500' />
                    <span className='text-green-600 font-semibold'>
                      Revenue Analytics
                    </span>
                  </div>
                }
              >
                {salesLoading ? (
                  <div className='flex flex-col justify-center items-center h-80'>
                    <Spin size='large' />
                    <div className='text-gray-500 mt-4'>
                      Loading sales data...
                    </div>
                  </div>
                ) : salesData && salesData.length > 0 ? (
                  <div className='h-96 p-4'>
                    <Line
                      data={getChartData()}
                      options={chartOptions}
                    />
                  </div>
                ) : (
                  <div className='text-center py-16'>
                    <div className='p-6 bg-gray-100 rounded-full w-24 h-24 mx-auto mb-6 flex items-center justify-center'>
                      <BarChartOutlined className='text-4xl text-gray-400' />
                    </div>
                    <h3 className='text-2xl font-semibold text-gray-600 mb-3'>
                      No Sales Data Available
                    </h3>
                    <p className='text-gray-500 text-lg'>
                      Start making sales to see your revenue trends here
                    </p>
                  </div>
                )}
              </Card>
            </Col>
          </Row>

          {/* Products and Spare Parts Tables - Side by Side */}
          <Row gutter={[24, 24]}>
            <Col
              xs={24}
              lg={12}
            >
              <Card
                className='h-full border-0 shadow-xl bg-white'
                title={
                  <div className='flex items-center gap-3'>
                    <div className='p-2 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg'>
                      <ProductOutlined className='text-white text-lg' />
                    </div>
                    <div>
                      <h3 className='text-lg font-bold text-gray-800'>
                        All Products
                      </h3>
                      <p className='text-gray-500 text-sm'>
                        Product performance overview
                      </p>
                    </div>
                  </div>
                }
                extra={
                  <Search
                    placeholder='Search products...'
                    allowClear
                    style={{ width: 220 }}
                    onChange={(e) => setProductSearchText(e.target.value)}
                    prefix={<SearchOutlined />}
                    className='rounded-lg'
                  />
                }
              >
                {productLoading ? (
                  <div className='flex flex-col justify-center items-center h-48'>
                    <Spin size='large' />
                    <div className='text-gray-500 mt-4'>
                      Loading products...
                    </div>
                  </div>
                ) : (
                  <Table
                    columns={productColumns}
                    dataSource={getFilteredProducts()}
                    rowKey='productId'
                    pagination={{
                      defaultPageSize: 8,
                      showSizeChanger: true,
                      showQuickJumper: true,
                      showTotal: (total, range) =>
                        `${range[0]}-${range[1]} of ${total} products`,
                      pageSizeOptions: ['8', '15', '30', '50'],
                      size: 'default',
                    }}
                    scroll={{ x: 400 }}
                    size='small'
                    className='custom-table'
                  />
                )}
              </Card>
            </Col>
            <Col
              xs={24}
              lg={12}
            >
              <Card
                className='h-full border-0 shadow-xl bg-white'
                title={
                  <div className='flex items-center gap-3'>
                    <div className='p-2 bg-gradient-to-r from-orange-500 to-red-500 rounded-lg'>
                      <ProductOutlined className='text-white text-lg' />
                    </div>
                    <div>
                      <h3 className='text-lg font-bold text-gray-800'>
                        All Spare Parts
                      </h3>
                      <p className='text-gray-500 text-sm'>
                        Spare parts inventory
                      </p>
                    </div>
                  </div>
                }
                extra={
                  <Search
                    placeholder='Search spare parts...'
                    allowClear
                    style={{ width: 220 }}
                    onChange={(e) => setSparePartSearchText(e.target.value)}
                    prefix={<SearchOutlined />}
                    className='rounded-lg'
                  />
                }
              >
                {productLoading ? (
                  <div className='flex flex-col justify-center items-center h-48'>
                    <Spin size='large' />
                    <div className='text-gray-500 mt-4'>
                      Loading spare parts...
                    </div>
                  </div>
                ) : (
                  <Table
                    columns={sparePartColumns}
                    dataSource={getFilteredSpareParts()}
                    rowKey='sparePartId'
                    pagination={{
                      defaultPageSize: 8,
                      showSizeChanger: true,
                      showQuickJumper: true,
                      showTotal: (total, range) =>
                        `${range[0]}-${range[1]} of ${total} spare parts`,
                      pageSizeOptions: ['8', '15', '30', '50'],
                      size: 'default',
                    }}
                    scroll={{ x: 400 }}
                    size='small'
                    className='custom-table'
                  />
                )}
              </Card>
            </Col>
          </Row>

          {/* Monthly Products Table */}
          <Row gutter={[24, 24]}>
            <Col xs={24}>
              <Card
                className='border-0 shadow-xl bg-white'
                title={
                  <div className='flex items-center gap-3'>
                    <div className='p-2 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-lg'>
                      <ShoppingCartOutlined className='text-white text-lg' />
                    </div>
                    <div>
                      <h3 className='text-lg font-bold text-gray-800'>
                        Monthly Products -{' '}
                        {monthlyData?.month ||
                          monthlyRanges[selectedMonth]?.month ||
                          'Select Month'}
                      </h3>
                      <p className='text-gray-500 text-sm'>
                        Top selling products this month
                      </p>
                    </div>
                  </div>
                }
                extra={
                  <div className='flex items-center gap-3'>
                    <Select
                      value={selectedMonth}
                      onChange={setSelectedMonth}
                      style={{ width: 200 }}
                      placeholder='Select Month'
                      className='rounded-lg'
                    >
                      {monthlyRanges.map((range, index) => (
                        <Option
                          key={index}
                          value={index}
                        >
                          {range.month}
                        </Option>
                      ))}
                    </Select>
                    <div className='flex items-center gap-2 text-blue-600'>
                      <BarChartOutlined />
                      <span className='text-sm font-medium'>Analytics</span>
                    </div>
                  </div>
                }
              >
                {monthlyLoading ? (
                  <div className='flex flex-col justify-center items-center h-48'>
                    <Spin size='large' />
                    <div className='text-gray-500 mt-4'>
                      Loading monthly data...
                    </div>
                  </div>
                ) : (
                  <Table
                    columns={monthlyProductColumns}
                    dataSource={getCurrentMonthProducts()}
                    rowKey='productId'
                    pagination={{
                      defaultPageSize: 10,
                      showSizeChanger: true,
                      showQuickJumper: true,
                      showTotal: (total, range) =>
                        `${range[0]}-${range[1]} of ${total} products`,
                      pageSizeOptions: ['10', '20', '50', '100'],
                      size: 'default',
                    }}
                    scroll={{ x: 600 }}
                    className='custom-table'
                    locale={{
                      emptyText: (
                        <div className='text-center py-12'>
                          <ShoppingCartOutlined className='text-4xl text-gray-300 mb-4' />
                          <p className='text-gray-500'>
                            No products found for this month
                          </p>
                        </div>
                      ),
                    }}
                  />
                )}
              </Card>
            </Col>
          </Row>

          {/* Top Selling Companies - Grid Design */}
          <Row gutter={[24, 24]}>
            <Col xs={24}>
              <Card
                className='border-0 shadow-xl bg-white'
                title={
                  <div className='flex items-center gap-3'>
                    <div className='p-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg'>
                      <TeamOutlined className='text-white text-lg' />
                    </div>
                    <div>
                      <h3 className='text-lg font-bold text-gray-800'>
                        Top Selling Companies -{' '}
                        {companyData?.month ||
                          monthlyRanges[selectedCompanyMonth]?.month ||
                          'Select Month'}
                      </h3>
                      <p className='text-gray-500 text-sm'>
                        Company performance rankings
                      </p>
                    </div>
                  </div>
                }
                extra={
                  <div className='flex gap-3'>
                    <Select
                      value={selectedCompanyMonth}
                      onChange={setSelectedCompanyMonth}
                      style={{ width: 200 }}
                      placeholder='Select Month'
                      className='rounded-lg'
                    >
                      {monthlyRanges.map((range, index) => (
                        <Option
                          key={index}
                          value={index}
                        >
                          {range.month}
                        </Option>
                      ))}
                    </Select>
                    <div className='flex items-center gap-2 text-purple-600'>
                      <TrophyOutlined />
                      <span className='text-sm font-medium'>Rankings</span>
                    </div>
                  </div>
                }
              >
                {companyLoading ? (
                  <div className='flex flex-col justify-center items-center h-48'>
                    <Spin size='large' />
                    <div className='text-gray-500 mt-4'>
                      Loading company data...
                    </div>
                  </div>
                ) : companyData?.data && companyData.data.length > 0 ? (
                  <Row gutter={[20, 20]}>
                    {getFilteredCompanies().map((company, index) => (
                      <Col
                        xs={24}
                        sm={12}
                        lg={8}
                        xl={6}
                        key={company.companyId}
                      >
                        <Card
                          className={`h-full hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-1 ${
                            index === 0
                              ? 'border-2 border-yellow-400 bg-gradient-to-br from-yellow-50 to-orange-50 shadow-xl'
                              : index === 1
                                ? 'border-2 border-gray-300 bg-gradient-to-br from-gray-50 to-slate-50 shadow-lg'
                                : index === 2
                                  ? 'border-2 border-orange-300 bg-gradient-to-br from-orange-50 to-amber-50 shadow-lg'
                                  : 'border border-gray-200 hover:border-purple-300 bg-white'
                          }`}
                          styles={{ body: { padding: '20px' } }}
                        >
                          <div className='text-center'>
                            {/* Ranking Badge */}
                            <div className='flex items-center justify-center mb-4'>
                              <div
                                className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg shadow-lg ${
                                  index === 0
                                    ? 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-white'
                                    : index === 1
                                      ? 'bg-gradient-to-r from-gray-400 to-gray-500 text-white'
                                      : index === 2
                                        ? 'bg-gradient-to-r from-orange-400 to-orange-500 text-white'
                                        : 'bg-gradient-to-r from-purple-100 to-purple-200 text-purple-600'
                                }`}
                              >
                                {index < 3 ? index + 1 : `#${index + 1}`}
                              </div>
                            </div>

                            {/* Company Name */}
                            <h4
                              className='font-bold text-gray-800 mb-4 text-base truncate'
                              title={company.companyName}
                            >
                              {company.companyName}
                            </h4>

                            {/* Stats */}
                            <div className='space-y-3 mb-4'>
                              <div className='flex justify-between items-center'>
                                <span className='text-gray-600 text-sm'>
                                  Orders:
                                </span>
                                <span className='font-bold text-green-600 text-lg'>
                                  {company.totalOrders}
                                </span>
                              </div>
                              <div className='flex justify-between items-center'>
                                <span className='text-gray-600 text-sm'>
                                  Revenue:
                                </span>
                                <span className='font-bold text-blue-600 text-lg'>
                                  ₹
                                  {company.totalOrderAmount?.toLocaleString() ||
                                    0}
                                </span>
                              </div>
                            </div>

                            {/* Performance Indicator */}
                            <div className='mt-4'>
                              <Tag
                                color={
                                  index === 0
                                    ? 'gold'
                                    : index === 1
                                      ? 'silver'
                                      : index === 2
                                        ? 'orange'
                                        : 'purple'
                                }
                                className='text-sm font-medium px-3 py-1'
                              >
                                {index === 0
                                  ? '🥇 Top Performer'
                                  : index === 1
                                    ? '🥈 Runner Up'
                                    : index === 2
                                      ? '🥉 Third Place'
                                      : 'Rising Star'}
                              </Tag>
                            </div>
                          </div>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                ) : (
                  <div className='text-center py-16'>
                    <div className='p-6 bg-gray-100 rounded-full w-24 h-24 mx-auto mb-6 flex items-center justify-center'>
                      <TeamOutlined className='text-4xl text-gray-400' />
                    </div>
                    <h3 className='text-2xl font-semibold text-gray-600 mb-3'>
                      No Company Data Available
                    </h3>
                    <p className='text-gray-500 text-lg'>
                      No companies found for the selected month
                    </p>
                  </div>
                )}
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </CommonLayout>
  );
}

export default Home;
